import type {
  CreatePlacementDto,
  PatchPlacementDto,
  Placement,
  PlacementHistoryQuery,
  PlacementResponse,
  PlacementsQuery,
  Schedule,
} from "../../interface/schedule"
import type { Page, PlacementId } from "../../interface/publications"
import type { Occurrence } from "../../interface/occurrences"
import { assertPatch, businessApi, postBotTags, requestQuery } from "./api"

const scheduleBody = (schedule: Schedule): Schedule => ({
  schedules: schedule.schedules.map((rule) =>
    rule.type === "interval"
      ? { type: rule.type, period: rule.period, value: rule.value }
      : {
          type: rule.type,
          weekDays: rule.weekDays,
          hour: rule.hour,
          minute: rule.minute,
          tz: rule.tz,
        },
  ),
  TZ: schedule.TZ,
  dateStart: schedule.dateStart,
  dateEnd: schedule.dateEnd,
})

const transformPlacementResponse = (response: PlacementResponse): Placement =>
  response.placement

export const placementsApi = businessApi.injectEndpoints({
  endpoints: (build) => ({
    createPlacement: build.mutation<Placement, CreatePlacementDto>({
      queryFn: requestQuery(
        (body) => ({
          url: "placements",
          method: "POST",
          body: {
            publicationId: body.publicationId,
            channelId: body.channelId,
            schedule: scheduleBody(body.schedule),
            enabled: body.enabled,
            pin: body.pin,
            deletePrevious: body.deletePrevious,
          },
        }),
        transformPlacementResponse,
      ),
      invalidatesTags: (_result, error) =>
        error ? [] : [postBotTags.placements],
    }),
    getPlacements: build.query<Page<Placement>, PlacementsQuery | void>({
      query: (args) => ({
        url: "placements",
        params: {
          page: args?.page,
          limit: args?.limit,
          publicationId: args?.publicationId,
          channelId: args?.channelId,
          enabled: args?.enabled,
          status: args?.status,
        },
      }),
      providesTags: (result) => [
        postBotTags.placements,
        ...(result?.data.map(({ id }) => postBotTags.placement(id)) ?? []),
      ],
    }),
    getPlacement: build.query<Placement, PlacementId>({
      query: (id) => `placements/${id}`,
      transformResponse: transformPlacementResponse,
      providesTags: (_result, _error, id) => [postBotTags.placement(id)],
    }),
    patchPlacement: build.mutation<
      Placement,
      { id: PlacementId; body: PatchPlacementDto }
    >({
      queryFn: requestQuery(({ id, body }) => {
        assertPatch(body.revision, [
          body.channelId,
          body.schedule,
          body.enabled,
          body.pin,
          body.deletePrevious,
        ])
        return {
          url: `placements/${id}`,
          method: "PATCH",
          body: {
            revision: body.revision,
            channelId: body.channelId,
            schedule:
              body.schedule === undefined
                ? undefined
                : scheduleBody(body.schedule),
            enabled: body.enabled,
            pin: body.pin,
            deletePrevious: body.deletePrevious,
          },
        }
      }, transformPlacementResponse),
      invalidatesTags: (_result, error, { id }) =>
        error ? [] : [postBotTags.placements, postBotTags.placement(id)],
    }),
    getPlacementHistory: build.query<Page<Occurrence>, PlacementHistoryQuery>({
      query: ({ id, page, limit }) => ({
        url: `placements/${id}/history`,
        params: { page, limit },
      }),
      providesTags: (result, _error, { id }) => [
        postBotTags.history(id),
        ...(result?.data.map(({ id }) => postBotTags.occurrence(id)) ?? []),
      ],
    }),
  }),
})

export const {
  useCreatePlacementMutation,
  useGetPlacementsQuery,
  useGetPlacementQuery,
  useLazyGetPlacementQuery,
  usePatchPlacementMutation,
  useGetPlacementHistoryQuery,
} = placementsApi
